<script setup>
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Button from '@/components/ui/button/Button.vue';
import { Link } from '@inertiajs/vue3';
import { X, AlertCircle, Info, CheckCircle, AlertTriangle } from 'lucide-vue-next';
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';

const props = defineProps({
    popups: {
        type: Array,
        default: () => [],
    },
});

const currentPopupIndex = ref(0);
const isOpen = ref(false);
const dismissedPopups = ref([]);
const timeoutId = ref(null);

// LocalStorage'dan daha önce kapatılanları yükle
onMounted(() => {
    const dismissed = localStorage.getItem('dismissed_popups');
    if (dismissed) {
        try {
            dismissedPopups.value = JSON.parse(dismissed);
        } catch (e) {
            dismissedPopups.value = [];
            localStorage.removeItem('dismissed_popups');
        }
    }

    // İlk popup'ı göster
    showNextPopup();
});

// Cleanup: Component unmount olduğunda timeout'ları temizle
onBeforeUnmount(() => {
    if (timeoutId.value) {
        clearTimeout(timeoutId.value);
        timeoutId.value = null;
    }
    // Dialog'u kapat
    isOpen.value = false;
});

const currentPopup = computed(() => {
    if (currentPopupIndex.value < filteredPopups.value.length) {
        return filteredPopups.value[currentPopupIndex.value];
    }
    return null;
});

// Daha önce kapatılmamış popup'ları filtrele
const filteredPopups = computed(() => {
    // Güvenlik: popups array mi kontrol et
    if (!Array.isArray(props.popups)) {
        return [];
    }

    return props.popups.filter((popup) => {
        // Güvenlik: popup objesinin gerekli alanları var mı?
        if (!popup || typeof popup !== 'object' || !popup.id) {
            return false;
        }

        // show_once aktifse ve daha önce kapatılmışsa gösterme
        if (popup.show_once && dismissedPopups.value.includes(popup.id)) {
            return false;
        }

        return true;
    });
});

const showNextPopup = () => {
    if (currentPopupIndex.value < filteredPopups.value.length) {
        isOpen.value = true;
    } else {
        isOpen.value = false;
    }
};

const closePopup = () => {
    if (currentPopup.value && currentPopup.value.show_once) {
        // LocalStorage'a ekle
        if (!dismissedPopups.value.includes(currentPopup.value.id)) {
            dismissedPopups.value.push(currentPopup.value.id);

            // LocalStorage limitini koru (son 100 popup ID'si)
            if (dismissedPopups.value.length > 100) {
                dismissedPopups.value = dismissedPopups.value.slice(-100);
            }

            try {
                localStorage.setItem('dismissed_popups', JSON.stringify(dismissedPopups.value));
            } catch (e) {
                // LocalStorage quota aşıldıysa eski verileri temizle
                console.error('LocalStorage hatası:', e);
                dismissedPopups.value = dismissedPopups.value.slice(-50);
                try {
                    localStorage.setItem('dismissed_popups', JSON.stringify(dismissedPopups.value));
                } catch (retryError) {
                    // Hala hata varsa tüm veriyi temizle
                    localStorage.removeItem('dismissed_popups');
                    dismissedPopups.value = [currentPopup.value.id];
                }
            }
        }
    }

    isOpen.value = false;
    currentPopupIndex.value++;

    // Önceki timeout'u temizle
    if (timeoutId.value) {
        clearTimeout(timeoutId.value);
    }

    // Sonraki popup'ı göster (varsa)
    timeoutId.value = setTimeout(() => {
        showNextPopup();
        timeoutId.value = null;
    }, 300);
};

const getButtonVariant = (type) => {
    switch (type) {
        case 'success':
            return 'default';
        case 'warning':
            return 'default';
        case 'error':
            return 'destructive';
        default:
            return 'default';
    }
};

// Image URL oluştur
const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    // Eğer zaten tam URL ise direkt dön
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
    }
    // storage/app/public altındaki dosyalar için
    return `/storage/${imagePath}`;
};

// Scrollbar tıklamasında popup'ın kapanmasını engelle
const handlePointerDownOutside = (event) => {
    const originalEvent = event.detail.originalEvent;
    const target = originalEvent.target;

    // Scrollbar tıklamasını tespit et
    if (originalEvent.offsetX > target.clientWidth || originalEvent.offsetY > target.clientHeight) {
        event.preventDefault();
        return;
    }

    // Normal dışarı tıklama durumunda popup'ı kapat
    // (Kullanıcı backdrop'a tıkladığında)
};

// Type'a göre icon döndür
const getTypeIcon = (type) => {
    switch (type) {
        case 'success':
            return CheckCircle;
        case 'warning':
            return AlertTriangle;
        case 'error':
            return AlertCircle;
        case 'info':
        default:
            return Info;
    }
};

// Type'a göre icon rengi döndür
const getTypeIconClass = (type) => {
    switch (type) {
        case 'success':
            return 'text-green-600 dark:text-green-400';
        case 'warning':
            return 'text-yellow-600 dark:text-yellow-400';
        case 'error':
            return 'text-red-600 dark:text-red-400';
        case 'info':
        default:
            return 'text-blue-600 dark:text-blue-400';
    }
};

// Type'a göre border rengi döndür
const getTypeBorderClass = (type) => {
    switch (type) {
        case 'success':
            return 'border-l-4 border-l-green-600 dark:border-l-green-400';
        case 'warning':
            return 'border-l-4 border-l-yellow-600 dark:border-l-yellow-400';
        case 'error':
            return 'border-l-4 border-l-red-600 dark:border-l-red-400';
        case 'info':
        default:
            return 'border-l-4 border-l-blue-600 dark:border-l-blue-400';
    }
};
</script>

<template>
    <Dialog :open="isOpen" @update:open="(val) => !val && closePopup()">
        <DialogContent
            v-if="currentPopup"
            :class="['max-w-lg max-h-[90vh] overflow-y-auto', getTypeBorderClass(currentPopup.type)]"
            @pointer-down-outside="handlePointerDownOutside"
            :trap-focus="true"
        >
            <DialogHeader>
                <div class="flex items-start gap-4">
                    <!-- Type Icon -->
                    <component
                        :is="getTypeIcon(currentPopup.type)"
                        :class="['h-6 w-6 flex-shrink-0 mt-0.5', getTypeIconClass(currentPopup.type)]"
                        aria-hidden="true"
                    />

                    <div class="flex-1">
                        <DialogTitle class="text-lg font-semibold">
                            {{ currentPopup.title }}
                        </DialogTitle>
                    </div>

                    <button
                        @click="closePopup"
                        class="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none flex-shrink-0"
                        aria-label="Popup'ı kapat"
                        type="button"
                    >
                        <X class="h-5 w-5" />
                        <span class="sr-only">Kapat</span>
                    </button>
                </div>
            </DialogHeader>

            <!-- Image gösterimi (eğer varsa) -->
            <div v-if="currentPopup.image" class="py-2">
                <img
                    :src="getImageUrl(currentPopup.image)"
                    :alt="currentPopup.title"
                    class="w-full h-auto rounded-lg object-cover max-h-[300px]"
                    loading="lazy"
                />
            </div>

            <DialogDescription
                class="py-4 text-sm text-gray-700 dark:text-gray-300 prose dark:prose-invert max-w-none"
                v-html="currentPopup.content"
                role="document"
            ></DialogDescription>

            <DialogFooter v-if="currentPopup.button_text && currentPopup.button_link">
                <Link v-if="currentPopup.button_link.startsWith('/')" :href="currentPopup.button_link">
                    <Button :variant="getButtonVariant(currentPopup.type)" @click="closePopup">
                        {{ currentPopup.button_text }}
                    </Button>
                </Link>
                <a v-else :href="currentPopup.button_link" target="_blank" rel="noopener noreferrer">
                    <Button :variant="getButtonVariant(currentPopup.type)" @click="closePopup">
                        {{ currentPopup.button_text }}
                    </Button>
                </a>
            </DialogFooter>
            <DialogFooter v-else>
                <Button @click="closePopup">Tamam</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
